import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { PdfToImageService } from '../../core/pipes/pdf-converter/pdf-image.pipe';
import { PosterService } from '../../features/poster/poster.service';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import {
  noOnlySpacesValidator,
  noOnlySpecialCharsAlphaValidator,
} from '../../core/helpers/validator';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import { FilenameGeneratorUtil } from '../../core/utils/filename-generator';

@Component({
  selector: 'app-sketches-list-pages',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    FullscreenLoaderComponent,
    ContentLoaderComponent,
  ],
  templateUrl: './new-poster.component.html',
  styleUrl: './new-poster.component.scss',
})
export class NewPosterComponent {
  @ViewChild('imagefileInput') imagefileInput!: ElementRef<HTMLInputElement>;
  readonly storage = inject(Storage);
  readonly dialog = inject(MatDialog);
  public mainForm: FormGroup;
  public languages: any[] = [];
  public TAGS: any[] = [];
  public selectedTags: any[] = [];
  public langCode: String = '';
  public posterTranslations: {
    id?: number;
    title: string;
    description: string;
    langId: number;
  }[] = [];

  imageUrl: string | null = null;
  imageUploading = false;
  imageFileName: string | null = null;
  posterId: any;
  currTranslationId: any;
  loading = false;
  isSubmitting = false;
  contentLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private posterService: PosterService,
    private tagsLanguageService: TagsLanguageService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private pdfToImageService: PdfToImageService
  ) {
    this.mainForm = this.fb.group({
      title: [
        '',
        [
          Validators.required,
          noOnlySpecialCharsAlphaValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      description: [
        '',
        [
          Validators.required,
          noOnlySpecialCharsAlphaValidator(),
          noOnlySpacesValidator(),
        ],
      ],
      langId: [1, Validators.required],
    });
  }

  ngOnInit() {
    this.getLanguages();
    this.getAllTagsInitially(this.langCode);

    this.route.paramMap.subscribe((params) => {
      this.posterId = params.get('id');
      if (this.posterId) {
        this.contentLoading = true;
      }
    });
  }

  getLanguages(): void {
    this.tagsLanguageService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onCancel() {
    this.router.navigate(['/poster']);
  }

  getPosterById(id: any, lang: any): void {
    this.posterService.getPosterById(id, lang).subscribe({
      next: (res) => {
        const poster = res.poster.poster;

        this.imageUrl = poster.image;

        if (poster.image) {
          // Use the utility instead of local method
          this.imageFileName = FilenameGeneratorUtil.generateRandomFilename(poster.image);
        }

        const selectedTagObjects = this.TAGS?.filter((tag) =>
          poster.tags?.some((selectedTag: any) => selectedTag?.id === tag.id)
        );
        this.selectedTags = selectedTagObjects;

        this.mainForm.patchValue({
          langId: null,
        });

        poster.posterTranslations.forEach((translation: any) => {
          this.posterTranslations.push({
            langId: translation.langId,
            id: translation.id,
            title: translation.title,
            description: translation.description,
          });
        });

        this.contentLoading = false;
      },
      error: (err) => {
        this.contentLoading = false;
      },
    });
  }
  removePosterTranslation(index: number): void {
    this.currTranslationId = this.posterTranslations[index]?.id;
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        if (this.currTranslationId) {
          this.posterService
            .deletePosterTranslation(this.currTranslationId)
            .subscribe({
              next: (res) => {
                this.posterTranslations.splice(index, 1);
              },
              error: (err) => {
                console.log(err);
              },
            });
        } else {
          this.posterTranslations.splice(index, 1);
        }
      }
    });
  }

  editPosterTranslation(index: number): void {
    const selected = this.posterTranslations[index];

    this.currTranslationId = selected.id;

const currentTitle = this.mainForm.get('title')?.value;
const currentDescription = this.mainForm.get('description')?.value;

if (currentTitle?.trim() && currentDescription?.trim()) {
  this.addPosterTable();
}

    this.mainForm.get('title')?.enable();
    this.mainForm.get('description')?.enable();
    this.mainForm.get('langId')?.enable();

    this.posterTranslations.splice(index, 1);

    this.mainForm.patchValue({
      title: selected.title,
      description: selected.description,
      langId: selected.langId,
    });

    this.onLanguageChange(selected.langId);
    this.mainForm.get('title')?.markAsTouched();
    this.mainForm.get('langId')?.markAsTouched();
    this.scrollUp(`translation`);
  }

  scrollUp(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  getAllTagsInitially(lang: any): void {
    this.tagsLanguageService.getAllTagsWithFilter(lang).subscribe({
      next: (res) => {
        if (res?.data) {
          this.TAGS = res.data
            .filter((tag: any) => tag.type === 'poster')
            .map((tag: any) => {
              const translation = tag.tagTranslation?.[0];
              return translation ? { ...tag, name: translation.tagName } : null;
            })
            .filter((tag: any) => tag !== null);
        }

        if (this.posterId) {
          this.getPosterById(this.posterId, this.langCode);
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getAllTags(lang: any): void {
    this.tagsLanguageService.getAllTagsWithFilter(lang).subscribe({
      next: (res) => {
        if (res?.data) {
          this.TAGS = res.data
            .filter((tag: any) => tag.type === 'poster')
            .map((tag: any) => {
              const translation = tag.tagTranslation?.[0];
              return translation ? { ...tag, name: translation.tagName } : null;
            })
            .filter((tag: any) => tag !== null);
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  removeTag(tagToRemove: any): void {
    this.selectedTags = this.selectedTags.filter(
      (tag) => tag.id !== tagToRemove.id
    );
  }

  isTagSelected(tag: any): boolean {
    return this.selectedTags?.some((t: any) => t.id === tag.id);
  }

  toggleTag(tag: any): void {
    const index = this.selectedTags.findIndex((t: any) => t.id === tag.id);

    if (index === -1) {
      this.selectedTags = [...this.selectedTags, tag];
    } else {
      this.selectedTags = this.selectedTags.filter((t: any) => t.id !== tag.id);
    }
  }

  async onImageUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'application/pdf',
    ];
    const maxSize = 21 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      this.toastr.error('Please choose a valid image or PDF file');
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      this.toastr.error('Choose a smaller image file (upto 20 MB)');
      input.value = '';
      return;
    }

    this.imageUploading = true;

    if (!navigator.onLine) {
      this.toastr.warning(
        'No internet connection. Please check your connection and try again.'
      );
      input.value = '';
      this.imageUploading = false;
      return;
    }

    try {
      let fileToUpload = file;
      let uploadName = file.name;
      if (file.type === 'application/pdf') {
        const pngBlob =
          await this.pdfToImageService.convertLocalPdfFirstPageToImage(file);
        fileToUpload = new File(
          [pngBlob],
          file.name.replace(/\.pdf$/, '.png'),
          { type: 'image/png' }
        );
        uploadName = fileToUpload.name;
      }

      const filePath = `poster-images/${uploadName}_${Date.now()}`;
      const fileRef = ref(this.storage, filePath);

      await uploadBytes(fileRef, fileToUpload);

      if (!navigator.onLine) {
        input.value = '';
        this.toastr.warning('Upload failed. No internet connection');
        this.imageUploading = false;
        return;
      }
      this.imageUrl = await getDownloadURL(fileRef);
      this.imageFileName = uploadName;
    } catch (err) {
      this.toastr.error(
        'Upload failed due to an internal error. Please try again later.'
      );
    } finally {
      this.imageUploading = false;
    }
  }

  clearImageUpload(): void {
    this.imageFileName = null;
    this.imageUrl = null;
    if (this.imagefileInput) {
      this.imagefileInput.nativeElement.value = '';
    }
  }

  getLanguageName(langId: number): string {
    const lang = this.languages.find((l) => l.id === langId);
    return lang ? lang.name : `Lang ID: ${langId}`;
  }

  isLangUsed(langId: number): boolean {
    return this.posterTranslations?.some((t) => t.langId === langId);
  }

  onLanguageSelect(event: MatSelectChange): void {
    const selectedId = event.value;
    this.langCode = event.value;
    const selectedLang = this.languages.find((lang) => lang.id === selectedId);
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTags(this.langCode);
    }
  }

  onLanguageChange(id: any) {
    const selectedId = id;
    const selectedLang = this.languages.find((lang) => lang.id === selectedId);
    if (selectedLang) {
      this.langCode = selectedLang.code;
      this.getAllTags(this.langCode);
    }
  }

  addPosterTable(): void {
  const title = this.mainForm.get('title')?.value?.trim();
  const description = this.mainForm.get('description')?.value?.trim();
  const langId = this.mainForm.get('langId')?.value;

  if (!title || !description) {
    this.toastr.warning('Please enter title and description');
    return;
  }

  if (!langId) {
    this.toastr.warning('Please choose a valid language');
    return;
  }

  if (this.mainForm.invalid) {
    this.toastr.warning('Please enter valid data');
    return;
  }

  const isDuplicate = this.posterTranslations.some(
    (t) => t.langId === langId
  );

  if (isDuplicate) {
    this.toastr.error('This language has already been added.');
    return;
  }

  const translationToAdd: any = {
    title,
    description,
    langId,
  };

  if (this.currTranslationId) {
    translationToAdd.id = this.currTranslationId;
  }

  this.posterTranslations.push(translationToAdd);
  this.currTranslationId = null;

  this.mainForm.reset();
}


  onSave() {
    if (!this.imageUrl) {
      this.toastr.warning('Please upload an image');
      return;
    }

    const englishTranslation = this.posterTranslations.find(
      (t) => t.langId === 1
    );

    if (!englishTranslation) {
      this.toastr.warning(
        'Poster in English is mandatory, Please add it to continue'
      );
      this.scrollUp(`translation`);
      return;
    }

    const payload: any = {
      categoryId: 2,
      rating: 0.0,
      image: this.imageUrl,
      tags: this.selectedTags.map((tag) => tag.id),
      posterTranslations: this.posterTranslations,
    };

    if (payload.tags.length === 0) {
      this.toastr.warning('Please choose at least one tag');
      return;
    }

    this.loading = true;
    this.isSubmitting = true;

    if (!this.posterId) {
      this.posterService.createPoster(payload).subscribe({
        next: (res) => {
          this.toastr.success('Poster created successfully!');
          this.isSubmitting = false;
          this.router.navigate(['/poster']);
        },
        error: (err) => {
          this.loading = false;
          this.isSubmitting = false;
        },
        complete: () => {
          this.isSubmitting = false;
          this.loading = false;
        },
      });
    } else {
      payload.id = Number(this.posterId);
      this.posterService.updatePoster(payload).subscribe({
        next: (res) => {
          this.toastr.success(res.message);
          this.isSubmitting = false;
          this.router.navigate(['/poster']);
        },
        error: (err) => {
          console.error('Failed to update poster', err);
          this.loading = false;
          this.isSubmitting = false;
        },
        complete: () => {
          this.isSubmitting = false;
          this.loading = false;
        },
      });
    }
  }
}
